/**
 * The nine kinds of history, and what each one carries.
 *
 * `HISTORY_KINDS` is the table the form renders from and the model reads —
 * which fields a kind has, which are required, how it is dated and what its
 * card says. The method events sit beside it because a sale's card reads its
 * verb off the method rather than the kind, and the tone vocabulary is what
 * both tables share. Nothing here imports the model; the model asserts its
 * draft shape against `HistoryTextKey` instead.
 */
import type {
  LoanHistoryAct,
  ProvenanceEntryKind,
  ProvenanceMethod,
} from "@ondo/shared";

/**
 * What each acquisition method is, read as an event on a timeline.
 *
 * The chain of title is a list of ownership links, and the card at the top of
 * each one says what happened rather than repeating the method twice. `action`
 * is that sentence's verb, `tone` groups the methods into the three things that
 * can happen to a work — it was made, it was bought, it changed hands another
 * way — and `glyph` is the mark on the rail.
 *
 * THE TONES CARRY NO JUDGEMENT. A bequest is not weaker than a purchase; how
 * good the evidence is, is the pill beside it, and who has put their name to it
 * is the count below. Three separate signals, deliberately, because collapsing
 * any two of them would have the timeline asserting something the registry does
 * not know.
 */
export const METHOD_EVENTS: Record<
  ProvenanceMethod,
  { action: string; tone: EventTone; glyph: string }
> = {
  created_by_artist: { action: "Created", tone: "created", glyph: "✦" },
  commission: { action: "Commissioned", tone: "created", glyph: "✦" },
  purchase: { action: "Acquired", tone: "acquired", glyph: "◉" },
  auction: { action: "Bought at auction", tone: "acquired", glyph: "◉" },
  exchange: { action: "Exchanged", tone: "transferred", glyph: "→" },
  gift: { action: "Received as a gift", tone: "transferred", glyph: "→" },
  bequest: { action: "Bequeathed", tone: "transferred", glyph: "→" },
  inheritance: { action: "Inherited", tone: "transferred", glyph: "→" },
  consignment: { action: "Consigned", tone: "transferred", glyph: "→" },
  restitution: { action: "Restituted", tone: "transferred", glyph: "→" },
  unknown: { action: "Held", tone: "unknown", glyph: "◇" },
};

/**
 * The colour families a history card can wear.
 *
 * Four of these existed when every entry was a link in a chain of title and
 * described how title moved. The five added with the eight kinds describe
 * things that happen to a work WITHOUT title moving, which is why they are
 * tones of their own rather than shades of `transferred`.
 *
 * THE TONES CARRY NO JUDGEMENT, and that rule did not change: a condition
 * report is not weaker than a sale. How good the evidence is, is the pill; who
 * has put their name to it is the count.
 */
export type EventTone =
  | "created"
  | "acquired"
  | "transferred"
  | "shown"
  | "published"
  | "recorded"
  | "assessed"
  | "moved"
  | "insured"
  | "unknown";

// ──────────────────────────────────────────────── the nine kinds of history ──

/**
 * A field on the history form, named by the draft key it writes.
 *
 * The keys are declared HERE and not in the model, because `HISTORY_KINDS`
 * below is the table the form renders from and the model reads — and constants
 * may not import the model. `DraftEntry` carries a property per key, which the
 * model asserts against this type rather than restating.
 */
export type HistoryTextKey =
  | "owner"
  | "counterparty"
  | "venue"
  | "title"
  | "reference"
  | "person"
  | "publisher"
  | "price"
  | "gallery"
  | "location"
  | "insurer"
  | "policy"
  /**
   * THE AUTHORSHIP CLAIM, IN WORDS. `attribution` only, and the one text key
   * nobody types — see `fixed` below.
   */
  | "claimedArtistName";

/**
 * One control on the form.
 *
 * A union rather than a `kind: string` with optional everything, so the two
 * selects cannot arrive without the list they select from and a text field
 * cannot arrive with one.
 */
export type HistoryField =
  | {
      control: "text";
      key: HistoryTextKey;
      label: string;
      placeholder: string;
      required: boolean;
      /** The '+ This owner is on ONDO' affordance sits under this field. */
      ondo?: true;
      /**
       * THE FORM SHOWS THIS VALUE AND THE PERSON CANNOT EDIT IT. It is read off
       * the record the entry is being written onto, not typed.
       *
       * Why a whole field flavour rather than a read-only attribute on one
       * input: the words of an authorship claim MUST match the registry artist
       * the claim's id points at. The id is what makes the claim answerable — an
       * authority is appointed over an `ondo.artists` row — and the words are
       * what the entry freezes and a studio signs. Let somebody type the words
       * and the two halves come apart: a studio with authority over Erwin Wurm
       * is shown a claim whose id is Wurm's and whose sentence says Jeff Koons,
       * and signing it puts the studio's name on the wrong artist's work.
       *
       * NO CHECK CONSTRAINT CAN CATCH THAT, which is why it is stopped here.
       * `provenance_entries_attribution_names_its_artist` requires both columns
       * to be present and says nothing about them agreeing — deliberately, per
       * 20261004096000 decision 3: a claim that contradicts the work's own
       * `artist_id` "is the interesting case rather than the invalid one",
       * because it is how a disputed attribution gets recorded at all. So the
       * database cannot refuse a mismatch without also refusing the honest
       * disagreement, and the client is the only place the two can be kept in
       * step at the moment they are written.
       *
       * Rejected: a searchable artist field, which is what the design draws for
       * the artist on the register wizard. There is nothing to search here — the
       * work already names its artist, and offering a second chance to name one
       * would invite somebody to ask a studio about a work whose record says
       * somebody else made it, with no way to tell which half they meant.
       */
      fixed?: true;
    }
  | { control: "method"; label: string }
  | { control: "documentKind"; label: string }
  /**
   * An amount and the currency it counts, as ONE field.
   *
   * Together rather than as two `text` entries, because they are one fact and
   * the database says so: `provenance_entries_insured_names_its_currency`
   * refuses a sum with no currency beside it. Two independent text fields would
   * let the form submit half of it and meet that constraint as a 422 the
   * collector cannot act on.
   *
   * The only control that reads and writes a NUMBER, so `toEntryPayload` parses
   * it and `validateEntry` has a branch of its own for it.
   */
  | { control: "money"; label: string; help: string };

/**
 * The verb on a loan entry the REGISTRY watched happen.
 *
 * `METHOD_EVENTS`' argument, applied to the other kind that has more than one
 * thing to say. That table exists because "the design offers no chip for a gift,
 * a bequest, an inheritance or a restitution -- they are Sale with a method --
 * and if the card read its verb off the kind, a bequest would appear on the
 * timeline as 'Sale', which is a different claim about how a family came by a
 * picture."
 *
 * A loan is now in the same position. Since OND-9 a loan's life writes up to
 * three entries -- the museum agreed, the work arrived, the work went back --
 * and all three are kind `loan`. Reading the verb off the kind would render
 * three rows all saying "Loaned", which is not three claims but one claim
 * printed three times, and a confirmation on any of them would mean nothing in
 * particular.
 *
 * NULL `loanAct` IS THE ORDINARY CASE and falls through to the kind's own verb:
 * a person who writes a loan entry by hand is describing the arrangement as a
 * whole, and "Loaned" is exactly right for that.
 *
 * The tone stays `shown` on all three -- the tones carry no judgement, and a
 * receipt is not a better fact than an agreement.
 */
export const LOAN_ACT_EVENTS: Record<
  LoanHistoryAct,
  { action: string; badge: string; glyph: string }
> = {
  agreed: { action: "Loan agreed", badge: "LOAN AGREED", glyph: "⇄" },
  received: { action: "Received on loan", badge: "RECEIVED", glyph: "▣" },
  returned: { action: "Returned from loan", badge: "RETURNED", glyph: "↩" },
};

export interface HistoryKindModel {
  /** The chip in the modal. */
  chip: string;
  glyph: string;
  tone: EventTone;
  /**
   * The verb on the timeline card, and the badge beside it.
   *
   * `sale` reads its verb off the METHOD instead — see `METHOD_EVENTS` — which
   * is what lets 'Received as a gift' and 'Bequeathed' appear on a timeline
   * whose only title-moving chip says Sale.
   *
   * And a `loan` the registry watched reads its verb off the ACT — see
   * `LOAN_ACT_EVENTS` — so an agreement, an arrival and a return are three
   * sentences rather than three copies of 'Loaned'. A loan entry somebody wrote
   * by hand has no act and uses this verb, which is the right one for the
   * arrangement as a whole.
   */
  action: string;
  badge: string;
  /**
   * Whether this kind spans time or happens at one moment.
   *
   * A range renders two date cascades, a point renders one. Not cosmetic: a
   * sale has a date and an exhibition has a run, and offering an end date on a
   * sale invites somebody to record a fact that does not exist.
   */
  dates: "range" | "point";
  dateLabels: { start: string; end: string | null; endHelp: string | null };
  /** Which text field carries the headline name on the card and in the list. */
  headline: HistoryTextKey;
  fields: readonly HistoryField[];
  notes: { label: string; placeholder: string; required: boolean };
  /**
   * Why this kind cannot be recorded when the record has nothing to fill its
   * `fixed` field from. Present on exactly the kinds that carry one.
   *
   * On the kind rather than in a sentence the chip row keeps for itself,
   * because the reason is a fact about the kind: a tenth kind with a fixed
   * field would need its own explanation, and a shared one would have to be
   * written vaguely enough to cover both. The chip row reads this and does not
   * know which kind it came from — that is what keeps the `switch (kind)` out
   * of `src/provenance/`.
   */
  needsFixedValue?: string;
}

/**
 * WHAT EACH KIND OF HISTORY IS MADE OF. The one table three things read.
 *
 * The form renders from it, the model validates against it and builds its
 * request body from it, and the timeline reads its verb and glyph off it. That
 * is deliberate and it is the reason there is no `switch (kind)` anywhere in
 * `src/provenance/`: a new kind is an entry in this object, a member in
 * `addProvenanceEntrySchema`, and a branch in the check constraint — three
 * places that can be checked against each other, rather than nine files that
 * cannot. OND-95's `insurance` is the ninth and arrived exactly that way; the
 * one thing it needed beyond the three was a `money` control, because it is the
 * first kind to carry a figure the registry can compare.
 *
 * The per-kind rule here is the CLIENT'S half of a rule the database owns. What
 * is required and what is refused is finally decided by
 * `provenance_entries_kind_requires` / `_kind_refuses` in
 * 20260820090100_artwork_history.sql; this exists so a collector is told which
 * box to fill in before they press the button, not after.
 *
 * ORDERED AS THE MODAL DRAWS THE CHIPS, which is the design's order and not the
 * enum's. `PROVENANCE_ENTRY_KINDS` runs as a work's life runs; this runs as a
 * collector scans a row of buttons.
 */
export const HISTORY_KINDS: Record<ProvenanceEntryKind, HistoryKindModel> = {
  /**
   * WHO MADE THE WORK, AND THE ONE KIND WHOSE PURPOSE IS TO BE ANSWERED.
   *
   * Every other kind records something that happened and may afterwards be
   * confirmed. This one exists so an authorised studio, estate or foundation
   * can accept or reject the authorship claim itself — OND-20, steps 3 to 5:
   * the registrant names the artist, which is their own attribution and
   * nothing more, and then EXPLICITLY ASKS the authority to confirm it.
   *
   * `tone: "created"` rather than a tenth tone. `created` is what
   * `created_by_artist` and `commission` already wear in `METHOD_EVENTS`, and
   * it names the axis this claim is on: the making of the object, as against
   * its being bought, shown, published, moved or assessed. A tone of its own
   * was rejected twice over — it would need a colour token in `index.css`
   * under a heading saying what that family of colours is for, and there is
   * nothing to say that `created` does not already say; and THE TONES CARRY NO
   * JUDGEMENT, so a colour reserved for authorship would be read as the one
   * that does.
   *
   * `dates: "point"`. A CLAIM IS MADE ON A DATE AND DOES NOT SPAN TIME. An
   * ownership runs from one year to another and a show opens and closes; "this
   * work is by Erwin Wurm" is either so or not so, and an end date on it would
   * invite somebody to record that the work stopped being by him — which is
   * not a fact this record can hold and not what a withdrawal means either.
   * Offering the field would be the auction's mistake made worse.
   *
   * ONE FIELD AND NO OTHER, and the field is `fixed`. The database refuses
   * every other column on this kind — `provenance_entries_kind_refuses`
   * untouched by 20261004096000, whose decision 4 lists what is left: the two
   * claim columns, the dates, `notes`, `evidence_claim` and `position`. No
   * owner, no venue, no price, no method, no document kind. "A claim about
   * authorship with a sale price on it would be a different claim."
   */
  attribution: {
    chip: "Artist attribution",
    // ✦ is `created`'s glyph in `METHOD_EVENTS`, and the mark the estate rail
    // already wears beside Authentication Requests — so the chip, the rail item
    // it ends up in and the timeline card are one symbol rather than three.
    glyph: "✦",
    tone: "created",
    action: "Attributed",
    badge: "ATTRIBUTION",
    dates: "point",
    // 'Dated', as `document` dates a paper: the day the claim is made as of,
    // which is usually the day its evidence is dated and is not the day the row
    // was written — `createdAt` holds that and nobody has to type it.
    dateLabels: { start: "Dated", end: null, endHelp: null },
    headline: "claimedArtistName",
    fields: [
      /**
       * Shown, never typed — see `fixed` on `HistoryField` for the forgery this
       * closes. The value is the work's own `artistId` / `attributedName`, so
       * what the studio is asked about and what the record already says are one
       * claim rather than two.
       */
      {
        control: "text",
        key: "claimedArtistName",
        label: "This work is by *",
        placeholder: "",
        required: true,
        fixed: true,
      },
    ],
    /**
     * The notes are the EVIDENCE, and the label asks for it in those words.
     *
     * Not required, and the restraint is the one `HISTORY_KINDS`' header states:
     * what is required here is the client's half of a rule the database owns,
     * and 20261004096000 added no `kind_requires` branch. A studio can be asked
     * on the strength of the attached documents alone, which is where a
     * certificate or a signature photograph actually goes; a required box would
     * be a client-only refusal the API would happily have accepted, and the
     * collector refused by it could not find out why.
     */
    notes: {
      label: "Your evidence for this attribution",
      placeholder:
        "What makes you say this is by the artist — a certificate, a signature, a gallery invoice, a published reference, the studio's own records…",
      required: false,
    },
    /**
     * Plain language, and it names the registry fact rather than the column.
     *
     * Authority is held over an `ondo.artists` row, so a work whose artist is
     * free text has nobody the registry would recognise as entitled to answer —
     * and offering the chip would produce an entry that could never be asked
     * about. 20261004096000 decision 3 keeps the claim's id required for
     * exactly this reason.
     */
    needsFixedValue:
      "Nobody can be asked about this work yet. The artist on it is a name typed into the record rather than one of the artists ONDO holds — and it is over an artist ONDO holds that a studio, estate or foundation is given the authority to answer. A work that names a listed artist can be sent for confirmation.",
  },

  exhibition: {
    chip: "Exhibition",
    glyph: "▥",
    tone: "shown",
    action: "Exhibited",
    badge: "EXHIBITION",
    dates: "range",
    dateLabels: {
      start: "Opened",
      end: "Closed",
      endHelp: "Leave blank if the show has no recorded closing date.",
    },
    headline: "title",
    fields: [
      {
        control: "text",
        key: "title",
        label: "Exhibition title *",
        placeholder: "e.g. Exposition Surréaliste",
        required: true,
      },
      {
        control: "text",
        key: "venue",
        label: "Venue / museum *",
        placeholder: "e.g. Galerie Pierre",
        required: true,
      },
      {
        control: "text",
        key: "location",
        label: "Location",
        placeholder: "City, Country",
        required: false,
      },
      {
        control: "text",
        key: "reference",
        label: "Catalogue no.",
        placeholder: "e.g. Cat. no. 7",
        required: false,
      },
    ],
    notes: {
      label: "Notes",
      placeholder: "Anything else worth recording about the showing…",
      required: false,
    },
  },

  /**
   * THE KIND IS `bibliography` AND THE WORD IS "SIGNIFICANT PUBLICATION" —
   * OND-38.
   *
   * The ticket asked whether naming the book is enough or whether a work needs
   * a structured citation with a volume and an entry number, and the answer was
   * the first: "use whatever format works", because "Ondo is meant to become
   * the catalogue raisonné for everything" — so this is not a pointer into
   * somebody else's authoritative catalogue, it is a record that the work
   * appears in a notable book. The enum value is not renamed, for the reason
   * `20261014090100_a_significant_publication_is_the_citation.sql` decision 3
   * gives: a feed outlives the deployment that wrote it and carries the kind as
   * a literal string.
   *
   * A CATALOGUE RAISONNÉ IS RECORDED HERE, with its volume in the title where
   * the book prints it and its pages in the reference. Nothing issues a
   * catalogue number from it: the work's number in this registry comes from a
   * confirmed authorship claim, which is the Artist attribution chip above.
   */
  bibliography: {
    chip: "Significant publication",
    glyph: "☐",
    tone: "published",
    action: "Published",
    badge: "PUBLICATION",
    dates: "point",
    dateLabels: { start: "Published", end: null, endHelp: null },
    headline: "title",
    fields: [
      {
        control: "text",
        key: "title",
        label: "Publication title *",
        placeholder: "e.g. Max Ernst: Catalogue Raisonné, Vol. II",
        required: true,
      },
      {
        control: "text",
        key: "person",
        label: "Author / editor",
        placeholder: "e.g. Werner Spies",
        required: false,
      },
      {
        control: "text",
        key: "publisher",
        label: "Publisher",
        placeholder: "e.g. DuMont",
        required: false,
      },
      {
        control: "text",
        key: "reference",
        // SEVERAL APPEARANCES IN ONE BOOK GO IN ONE LINE, and the placeholder
        // is the owner's own worked example for OND-38: a work appearing three
        // times in one book, "listing pages 252, 212 and 250. That's
        // adequate." Recording them as three lines is equally correct and
        // nothing here chooses — the field has no format, which is the answer.
        label: "Page or plate",
        placeholder: "e.g. p. 214, pl. 88, or pp. 252, 212, 250",
        required: false,
      },
    ],
    notes: {
      label: "Notes",
      placeholder: "What the publication says about this work…",
      required: false,
    },
  },

  document: {
    chip: "Document",
    glyph: "🗎",
    tone: "recorded",
    action: "Documented",
    badge: "DOCUMENT",
    dates: "point",
    dateLabels: { start: "Dated", end: null, endHelp: null },
    headline: "title",
    fields: [
      { control: "documentKind", label: "What this document is *" },
      {
        control: "text",
        key: "title",
        label: "Title / label *",
        placeholder: "e.g. Private sale agreement, Nov 2023",
        required: true,
      },
      {
        control: "text",
        key: "venue",
        label: "Issued by",
        placeholder: "e.g. Offer Waterman, London",
        required: false,
      },
    ],
    notes: {
      label: "Notes",
      placeholder: "What the document establishes…",
      required: false,
    },
  },

  condition: {
    chip: "Condition report",
    glyph: "🔍",
    tone: "assessed",
    action: "Assessed",
    badge: "CONDITION",
    dates: "point",
    dateLabels: { start: "Assessed", end: null, endHelp: null },
    headline: "person",
    fields: [
      {
        control: "text",
        key: "person",
        label: "Assessed by *",
        placeholder: "e.g. Dr Helena Braun, conservator",
        required: true,
      },
      {
        control: "text",
        key: "venue",
        label: "Institution / practice",
        placeholder: "e.g. Braun Conservation, Munich",
        required: false,
      },
      {
        control: "text",
        key: "reference",
        label: "Report ref.",
        placeholder: "e.g. BC-2021-114",
        required: false,
      },
      {
        control: "text",
        key: "location",
        label: "Location",
        placeholder: "City, Country",
        required: false,
      },
    ],
    /**
     * The one kind whose notes ARE the record.
     *
     * A condition report with no findings is a date and a name attached to
     * nothing — and it is the kind most likely to be attested to, because it is
     * already somebody putting their name to a statement about the object.
     */
    notes: {
      label: "Findings *",
      placeholder: "e.g. Stable condition; minor lacquer crazing on the left flank.",
      required: true,
    },
  },

  location: {
    chip: "Change of location",
    glyph: "✥",
    tone: "moved",
    action: "Moved",
    badge: "LOCATION",
    dates: "range",
    dateLabels: {
      start: "From",
      end: "Until",
      endHelp: "Leave blank if the work is still there.",
    },
    headline: "location",
    fields: [
      {
        control: "text",
        key: "location",
        label: "Where *",
        placeholder: "e.g. Geneva Freeport, Switzerland",
        required: true,
      },
      {
        control: "text",
        key: "venue",
        label: "Held by",
        placeholder: "e.g. Momart, London",
        required: false,
      },
    ],
    notes: {
      label: "Notes",
      placeholder: "Why it moved, and anything about the conditions…",
      required: false,
    },
  },

  sale: {
    chip: "Sale",
    glyph: "◉",
    tone: "acquired",
    /** Overridden per method — see `METHOD_EVENTS`. */
    action: "Acquired",
    badge: "OWNED",
    dates: "range",
    dateLabels: {
      start: "Start date",
      end: "End date",
      endHelp: "Leave blank if this owner still holds the work.",
    },
    headline: "owner",
    fields: [
      {
        control: "text",
        key: "owner",
        label: "New owner *",
        placeholder: "e.g. Peggy Guggenheim",
        required: true,
        ondo: true,
      },
      /**
       * The field that keeps a gift, a bequest, an inheritance and a
       * restitution recordable under a chip that says Sale. The card reads its
       * verb off this, so a gift filed here says 'Received as a gift' on the
       * timeline and never 'Sale'.
       */
      { control: "method", label: "How it changed hands *" },
      {
        control: "text",
        key: "counterparty",
        label: "Sold by",
        placeholder: "e.g. The artist’s studio",
        required: false,
      },
      {
        control: "text",
        key: "gallery",
        label: "Through",
        placeholder: "e.g. Galerie Gradiva",
        required: false,
      },
      {
        control: "text",
        key: "price",
        label: "Price",
        placeholder: "e.g. £1,480,000 — or ‘not disclosed’",
        required: false,
      },
      {
        control: "text",
        key: "location",
        label: "Location",
        placeholder: "City, Country",
        required: false,
      },
    ],
    notes: {
      label: "Notes",
      placeholder: "Additional context…",
      required: false,
    },
  },

  auction: {
    chip: "Auction",
    glyph: "⊞",
    tone: "acquired",
    action: "Sold at auction",
    badge: "AUCTION",
    dates: "point",
    dateLabels: { start: "Date of sale", end: null, endHelp: null },
    headline: "venue",
    fields: [
      {
        control: "text",
        key: "venue",
        label: "Auction house *",
        placeholder: "e.g. Sotheby’s London",
        required: true,
      },
      {
        control: "text",
        key: "title",
        label: "Sale title",
        placeholder: "e.g. Contemporary Art Evening Auction",
        required: false,
      },
      {
        control: "text",
        key: "reference",
        label: "Lot no.",
        placeholder: "e.g. Lot 27",
        required: false,
      },
      /**
       * Not required, and that is the honest shape. 'Sotheby’s London, Lot 31,
       * £42,000' is a complete provenance line in every catalogue in the trade;
       * who bought it is frequently not disclosed and inventing a required
       * field for it would mean somebody typing 'private collection' into a
       * column that means a named holder.
       */
      {
        control: "text",
        key: "owner",
        label: "New owner",
        placeholder: "Leave blank if the buyer is not known",
        required: false,
        ondo: true,
      },
      {
        control: "text",
        key: "counterparty",
        label: "Sold by",
        placeholder: "e.g. Property of a European private collection",
        required: false,
      },
      {
        control: "text",
        key: "price",
        label: "Price",
        placeholder: "e.g. £1,480,000 — or ‘bought in’",
        required: false,
      },
    ],
    notes: {
      label: "Notes",
      placeholder: "Additional context…",
      required: false,
    },
  },

  loan: {
    chip: "Loan",
    glyph: "⇄",
    tone: "shown",
    action: "Loaned",
    badge: "LOAN",
    dates: "range",
    dateLabels: {
      start: "Out",
      end: "Returned",
      endHelp: "Leave blank if the work is still out.",
    },
    headline: "venue",
    fields: [
      {
        control: "text",
        key: "venue",
        label: "Borrower / venue *",
        placeholder: "e.g. Tate Modern, London",
        required: true,
      },
      {
        control: "text",
        key: "counterparty",
        label: "Lender",
        placeholder: "e.g. The Hartwell Family Trust",
        required: false,
      },
      {
        control: "text",
        key: "title",
        label: "Exhibition title",
        placeholder: "e.g. The Surrealist Revolution",
        required: false,
      },
      {
        control: "text",
        key: "location",
        label: "Location",
        placeholder: "City, Country",
        required: false,
      },
    ],
    notes: {
      label: "Notes",
      placeholder: "Terms, duration, anything else recorded…",
      required: false,
    },
  },

  insurance: {
    chip: "Insurance",
    glyph: "⛨",
    tone: "insured",
    action: "Insured",
    badge: "INSURANCE",
    /**
     * A RANGE, and a blank end means the cover has no recorded end — not that
     * it lapsed. A policy runs from a date to a date, which is why the period
     * is the thing a confirmation attaches to: an insurer confirming "we
     * covered this work" with no dates would be confirming nothing in
     * particular.
     */
    dates: "range",
    dateLabels: {
      start: "Cover from",
      end: "Cover until",
      endHelp: "Leave blank if the policy has no recorded end date.",
    },
    headline: "insurer",
    fields: [
      {
        control: "text",
        key: "insurer",
        label: "Insurer *",
        placeholder: "e.g. AXA Art",
        required: true,
      },
      {
        control: "text",
        key: "policy",
        label: "Policy reference",
        placeholder: "e.g. 4471-B",
        required: false,
      },
      {
        control: "money",
        label: "Amount insured",
        help: "The sum the policy covers, and the currency it is written in.",
      },
      { control: "text", key: "location", label: "Location", placeholder: "City, Country", required: false },
    ],
    notes: {
      label: "Notes",
      placeholder: "Anything else recorded about the cover…",
      required: false,
    },
  },
};

/**
 * The chips, in the order the design draws them.
 *
 * `attribution` IS FIRST, and the eight after it keep the order they had.
 *
 * It is first because it is the only chip that asks a question instead of
 * recording something that already happened, and because it is the only one
 * that can be unavailable. A disabled control at the end of a wrapping row of
 * nine is a control nobody finds: a collector whose work names a free-text
 * artist would never learn that confirmation exists, which is the one thing
 * OND-20 is for. First, with its reason beside it, says both.
 *
 * Rejected: last, which is what leaving the eight alone would have given. The
 * design's order runs from the commonest thing a collector records to the
 * rarest, and authorship is neither — it is the question the whole record
 * hangs off. Nothing is preselected either way, so being first is not a
 * default; see `HISTORY_COPY`.
 */
export const HISTORY_KIND_ORDER: readonly ProvenanceEntryKind[] = [
  "attribution",
  "exhibition",
  "bibliography",
  "document",
  "condition",
  "location",
  "sale",
  "auction",
  "loan",
  "insurance",
];

/**
 * NO KIND IS PRESELECTED, and the fields appear once one is chosen.
 *
 * The one deliberate departure from the design, which opens with Exhibition
 * lit. An entry is frozen the moment it is saved and can afterwards only be
 * withdrawn — so a preselected chip is a default on a one-way door, and the
 * failure it produces is silent: a collector who fills in a sale under a chip
 * they never looked at gets an exhibition on their record and no way to edit
 * it. One extra click buys that back.
 */
export const HISTORY_COPY = {
  dialogTitle: "Add to artwork history",
  kindLegend: "Type of history",
  kindHelp: "Choose what you are recording. This cannot be changed afterwards.",
  chooseKind:
    "Pick what sort of history this is and the fields for it will appear here.",
  /**
   * Said under every `fixed` field, so a box somebody cannot type in is
   * explained where they meet it rather than left looking broken.
   *
   * One sentence for all of them, unlike `needsFixedValue`, because this says
   * the same thing whatever the field is: the value came off the record. What
   * is at stake if it could be edited is in `HistoryField.fixed`.
   */
  fixedHelp:
    "Taken from this work’s record. It is shown rather than typed, so what an authority is asked to confirm is the attribution the record already carries.",
  corroborateTitle: "Ask ONDO users to corroborate",
  corroborateHelp:
    "Invite others who can attest to this entry — a gallery, the previous owner, the exhibiting museum. They confirm on ONDO and the entry is marked verified.",
  /**
   * Said where the design has three avatar chips of suggested people.
   *
   * There is no name search on this platform and there is not going to be one
   * here: `principals_read` is `acts_as(id)`, nothing resolves a name to an
   * ONDO id, and a suggestion list assembled from other people's records is the
   * widest directory probe the product could grow. The id comes from the person
   * being asked, which is also what makes asking them a courtesy rather than a
   * lookup.
   */
  corroborateNoSearch:
    "You need their ONDO id. There is no name search — an ONDO id is attached, never discovered.",
  selfVerify: "I can attest to this myself",
  selfVerifyHelp:
    "Most chains of title are signed first by whoever wrote them. Your name goes on this entry alongside anyone else’s.",
  cancel: "Cancel",
  submit: "Add to history →",
  submitting: "Adding…",
  close: "Close",
} as const;
